import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import Button from '../common/Button.js';
import OrderPrintContent from '../common/OrderPrintContent.js';
import { createWaiter, updateWaiter, deleteWaiter, createWaiterCommission, getWaiterCommissions } from '../../services/waiterApi.js';
import { getOrders, getOrderById } from '../../services/orderApi.js';
import { getSessionDate } from '../../utils/dateHelpers.js';
import useAppStore from '../../store/useAppStore.js';
import useOrderStore from '../../store/branchstore/branchOrdersStore.js';
import useWaiterStore from '../../store/branchstore/branchWaiterStore.js';

const BranchManagerWaiterSection = () => {
  const [newWaiterName, setNewWaiterName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [todaysOrders, setTodaysOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [showWaiterManagement, setShowWaiterManagement] = useState(false);
  const [editingWaiterId, setEditingWaiterId] = useState(null);
  const [editingWaiterName, setEditingWaiterName] = useState('');
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);
  const [isPrintReady, setIsPrintReady] = useState(false);
  const [todaySearch, setTodaySearch] = useState('');
  const [commissionType, setCommissionType] = useState('percentage');
  const [commissionValue, setCommissionValue] = useState('');
  const [commissionOrderId, setCommissionOrderId] = useState(null);
  const [commissionResult, setCommissionResult] = useState(null);
  const [commissionDate, setCommissionDate] = useState(getSessionDate(new Date()));
  const [waiterFilter, setWaiterFilter] = useState('');
  const [waiterCommissionTotals, setWaiterCommissionTotals] = useState([]);
  const printRef = useRef();

  const {
    waiters,
    hasLoaded: waitersHasLoaded,
    loadWaiters,
    addWaiter: addWaiterToStore,
    updateWaiter: updateWaiterInStore,
    removeWaiter: removeWaiterFromStore
  } = useWaiterStore((state) => ({
    waiters: state.waiters,
    hasLoaded: state.hasLoaded,
    loadWaiters: state.loadWaiters,
    addWaiter: state.addWaiter,
    updateWaiter: state.updateWaiter,
    removeWaiter: state.removeWaiter
  }));

  const { user } = useAppStore((state) => ({
    user: state.user
  }));

  const updateOrderStatus = useOrderStore((state) => state.updateOrderStatus);

  useEffect(() => {
    if (!waitersHasLoaded) {
      loadWaiters();
    }
  }, [waitersHasLoaded, loadWaiters]);

  useEffect(() => {
    loadWaiterOrders();
  }, []);

  useEffect(() => {
    loadWaiterCommissionTotals();
  }, [commissionDate, waiterFilter]);

  const getWaiterName = (waiterIdValue) => {
    const waiter = waiters.find((waiter) => waiter.id === waiterIdValue);
    return waiter ? waiter.name : `#${waiterIdValue}`;
  };

  const orderMatchesSearch = (order, searchQuery) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const waiterName = getWaiterName(order.waiter_id).toLowerCase();
    return (
      order.id?.toString().toLowerCase().includes(query) ||
      order.total?.toString().toLowerCase().includes(query) ||
      waiterName.includes(query) ||
      (order.status || '').toLowerCase().includes(query)
    );
  };

  const filteredTodaysOrders = useMemo(
    () => todaysOrders.filter((order) => orderMatchesSearch(order, todaySearch)),
    [todaysOrders, todaySearch, waiters]
  );

  const waiterSummary = useMemo(() => {
    const todayRevenue = todaysOrders.reduce((sum, order) => sum + Number(order.total || 0), 0);
    return {
      totalWaiters: waiters.length,
      todayOrders: todaysOrders.length,
      todayRevenue,
      completedCount: todaysOrders.filter((order) => order.status === 'completed').length,
      cancelledCount: todaysOrders.filter((order) => order.status === 'cancelled').length
    };
  }, [waiters.length, todaysOrders]);

  const selectedCommissionOrder = useMemo(
    () => todaysOrders.find((order) => String(order.id) === String(commissionOrderId)) || null,
    [todaysOrders, commissionOrderId]
  );

  const calculateCommission = async () => {
    const value = Number(commissionValue);
    const orderAmount = Number(selectedCommissionOrder?.total || 0);
    if (!selectedCommissionOrder || orderAmount === 0 || Number.isNaN(value) || value < 0) {
      setCommissionResult(null);
      return;
    }

    const result = commissionType === 'percentage' ? (value / 100) * orderAmount : value;
    setCommissionResult(result);

    try {
      await createWaiterCommission({
        order_id: selectedCommissionOrder.id,
        waiter_id: selectedCommissionOrder.waiter_id,
        commission_type: commissionType,
        commission_value: value
      });
      await loadWaiterCommissionTotals();
    } catch (error) {
      console.error('Failed to save waiter commission', error);
    }
  };

  const loadWaiterOrders = async () => {
    try {
      setOrdersLoading(true);
      setOrdersError('');
      const businessDate = getSessionDate(new Date());
      const response = await getOrders({ businessDate, orderType: 'waiter', status: 'completed,cancelled' });
      const orders = response.orders || [];

      const todays = orders.filter((order) => (
        order.order_type === 'waiter' &&
        ['completed', 'cancelled'].includes(order.status) &&
        order.business_date === businessDate
      ));

      const todaysWithItems = await Promise.all(
        todays.map(async (baseOrder) => {
          try {
            const detailed = await getOrderById(baseOrder.id);
            return {
              ...baseOrder,
              items: detailed.order?.items || []
            };
          } catch (itemError) {
            console.error('Failed to load order items for order', baseOrder.id, itemError);
            return { ...baseOrder, items: [] };
          }
        })
      );

      setTodaysOrders(todaysWithItems);
    } catch (error) {
      setOrdersError('Failed to load waiter orders');
      console.error('Error loading waiter orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadWaiterCommissionTotals = async () => {
    try {
      const response = await getWaiterCommissions({
        businessDate: commissionDate,
        waiterName: waiterFilter || undefined,
        groupBy: 'waiter'
      });
      setWaiterCommissionTotals(response.commissions || []);
    } catch (error) {
      console.error('Failed to load waiter commission totals:', error);
      setWaiterCommissionTotals([]);
    }
  };

  const handleAddWaiter = async () => {
    const trimmed = newWaiterName.trim();
    if (!trimmed) return;

    try {
      setLoading(true);
      setError('');
      const response = await createWaiter({
        branch_id: user?.branch_id,
        name: trimmed,
        is_active: true
      });

      if (response.waiter) {
        addWaiterToStore(response.waiter);
      }
      setNewWaiterName('');
    } catch (error) {
      setError(error.message || 'Failed to add waiter');
      console.error('Error adding waiter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWaiter = (waiter) => {
    setEditingWaiterId(waiter.id);
    setEditingWaiterName(waiter.name);
  };

  const handleSaveEdit = async () => {
    if (!editingWaiterName.trim()) return;

    try {
      setLoading(true);
      const response = await updateWaiter(editingWaiterId, {
        name: editingWaiterName.trim()
      });

      const updatedWaiter = response.waiter || { id: editingWaiterId, name: editingWaiterName.trim() };
      updateWaiterInStore(editingWaiterId, updatedWaiter);
      setEditingWaiterId(null);
      setEditingWaiterName('');
    } catch (error) {
      setError(error.message || 'Failed to update waiter');
      console.error('Error updating waiter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWaiter = async (waiterId) => {
    if (!window.confirm('Are you sure you want to remove this waiter?')) return;

    try {
      setLoading(true);
      await deleteWaiter(waiterId);
      removeWaiterFromStore(waiterId);
    } catch (error) {
      setError(error.message || 'Failed to remove waiter');
      console.error('Error deleting waiter:', error);
    } finally {
      setLoading(false);
    }
  };

  const printHandler = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt-${new Date().getTime()}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 0;
      }
    `,
    onAfterPrint: () => {
      setCurrentPrintOrder(null);
      setIsPrintReady(false);
    }
  });

  useEffect(() => {
    if (!currentPrintOrder || !isPrintReady) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (printRef.current) {
        printHandler();
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [currentPrintOrder, isPrintReady, printHandler]);

  const handlePrint = (order) => {
    if (!order || !order.id) return;
    setCurrentPrintOrder(order);
    setIsPrintReady(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Today&apos;s Waiter Orders</h2>
            <p className="mt-2 text-sm text-gray-600">Completed and cancelled waiter orders for the current session</p>
          </div>
          <input
            type="text"
            value={todaySearch}
            onChange={(e) => setTodaySearch(e.target.value)}
            placeholder="Search by order #, waiter, status or amount"
            className="w-full max-w-md rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-3xl border border-gray-200 bg-blue-50 p-5">
            <p className="text-sm font-semibold text-blue-700">Waiters</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{waiterSummary.totalWaiters}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-5">
            <p className="text-sm font-semibold text-gray-700">Today&apos;s orders</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{waiterSummary.todayOrders}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-5">
            <p className="text-sm font-semibold text-gray-700">Completed</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{waiterSummary.completedCount}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-5">
            <p className="text-sm font-semibold text-gray-700">Today&apos;s revenue</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{waiterSummary.todayRevenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="mb-6 rounded-3xl border border-gray-200 bg-slate-50 p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Waiter Commission Calculator</h3>
              <p className="mt-1 text-sm text-gray-600">Calculate commission for a selected waiter order by percentage or fixed amount.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setCommissionType('percentage');
                setCommissionValue('');
                setCommissionOrderId(null);
                setCommissionResult(null);
              }}
              className="rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Reset calculator
            </button>
          </div>

          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-gray-700">Commission date</label>
              <input
                type="date"
                value={commissionDate}
                onChange={(e) => setCommissionDate(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-gray-700">Waiter name filter</label>
              <input
                type="text"
                value={waiterFilter}
                onChange={(e) => setWaiterFilter(e.target.value)}
                placeholder="Filter by waiter name"
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={loadWaiterCommissionTotals}
                className="w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition"
              >
                Load totals
              </button>
            </div>
          </div>

          <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Commission totals by waiter</p>
                <p className="mt-1 text-sm text-gray-500">Showing totals for {commissionDate} {waiterFilter ? `and waiter matching “${waiterFilter}”` : 'for all waiters'}.</p>
              </div>
            </div>
            {waiterCommissionTotals.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 p-5 text-sm text-gray-500">No commission totals found for the selected date and waiter filter.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 font-semibold text-gray-700">Waiter</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Total commission</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Entries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waiterCommissionTotals.map((row) => (
                      <tr key={`${row.waiter_id}-${row.business_date}`} className="border-b border-gray-200">
                        <td className="px-4 py-3 text-gray-700">{row.waiter_name || `Waiter #${row.waiter_id}`}</td>
                        <td className="px-4 py-3 text-gray-700">{row.business_date}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{Number(row.total_commission_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-700">{row.commission_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium text-gray-700">Select order</label>
                <select
                  value={commissionOrderId || ''}
                  onChange={(e) => setCommissionOrderId(e.target.value)}
                  className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Choose order</option>
                  {todaysOrders.map((order) => (
                    <option key={order.id} value={order.id}>
                      #{order.order_number || order.id} • {getWaiterName(order.waiter_id)} • {order.total || 0}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-gray-700">Commission type</label>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center gap-2 rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm hover:border-blue-500">
                    <input type="radio" name="commissionType" value="percentage" checked={commissionType === 'percentage'} onChange={() => setCommissionType('percentage')} className="h-4 w-4 text-blue-600" />
                    Percentage
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm hover:border-blue-500">
                    <input type="radio" name="commissionType" value="fixed" checked={commissionType === 'fixed'} onChange={() => setCommissionType('fixed')} className="h-4 w-4 text-blue-600" />
                    Fixed amount
                  </label>
                </div>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-gray-700">Value</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(e.target.value)}
                    placeholder={commissionType === 'percentage' ? 'Enter percentage' : 'Enter fixed amount'}
                    className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                    {commissionType === 'percentage' ? '%' : 'PKR'}
                  </span>
                </div>
              </div>

              <button type="button" onClick={calculateCommission} className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition">
                Calculate commission
              </button>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-5">
              <p className="text-sm font-medium text-gray-700">Commission result</p>
              <div className="mt-4 rounded-3xl bg-blue-50 p-4">
                <p className="text-sm text-gray-600">Order amount</p>
                <p className="mt-1 text-3xl font-bold text-slate-900">{selectedCommissionOrder ? Number(selectedCommissionOrder.total || 0).toFixed(2) : '0.00'}</p>
                <p className="mt-2 text-sm text-gray-500">{selectedCommissionOrder ? `Order #${selectedCommissionOrder.order_number || selectedCommissionOrder.id}` : 'Select an order to calculate commission'}</p>
              </div>
              <div className="mt-4 rounded-3xl bg-slate-100 p-4">
                <p className="text-sm text-gray-600">Commission amount</p>
                <p className="mt-1 text-3xl font-bold text-slate-900">{commissionResult !== null ? commissionResult.toFixed(2) : '0.00'}</p>
                <p className="mt-2 text-sm text-gray-500">{commissionType === 'percentage' ? `At ${commissionValue || 0}% of order value.` : 'Fixed commission amount.'}</p>
              </div>
            </div>
          </div>
        </div>

        {ordersError && (
          <div className="mb-4 rounded-2xl bg-red-50 p-3 text-sm text-red-700">{ordersError}</div>
        )}

        {ordersLoading ? (
          <div className="text-center text-gray-500">Loading orders...</div>
        ) : filteredTodaysOrders.length === 0 ? (
          <div className="rounded-2xl bg-gray-50 p-6 text-center">
            <p className="text-sm text-gray-500">No waiter orders match the current filter</p>
          </div>
        ) : (
          <div className="overflow-auto h-[400px]">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Order ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Waiter</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTodaysOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-200 hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">#{order.order_number || order.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{order.waiter_id ? getWaiterName(order.waiter_id) : 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{order.total || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${order.status === 'completed' ? 'bg-green-100 text-green-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handlePrint(order)} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition" title="Print receipt">🖨️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Waiters</h2>
            <p className="mt-2 text-sm text-gray-600">Add, edit, or remove waiters</p>
          </div>
          <Button variant="primary" onClick={() => setShowWaiterManagement(!showWaiterManagement)}>
            {showWaiterManagement ? 'Hide' : 'Show'} Waiters
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {showWaiterManagement && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Add New Waiter</h3>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <input
                  type="text"
                  value={newWaiterName}
                  onChange={(e) => setNewWaiterName(e.target.value)}
                  placeholder="Enter waiter name"
                  className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  disabled={loading}
                />
                <Button variant="primary" onClick={handleAddWaiter} disabled={loading || !newWaiterName.trim()}>
                  {loading ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              {waiters.length === 0 ? (
                <div className="bg-gray-50 p-6 text-center">
                  <p className="text-sm text-gray-500">No waiters added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Created Date</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waiters.map((waiter) => (
                        <tr key={waiter.id} className="border-b border-gray-200 hover:bg-gray-50 transition">
                          <td className="px-4 py-3">
                            {editingWaiterId === waiter.id ? (
                              <input
                                type="text"
                                value={editingWaiterName}
                                onChange={(e) => setEditingWaiterName(e.target.value)}
                                className="w-full max-w-xs rounded-lg border border-blue-300 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-100"
                                disabled={loading}
                              />
                            ) : (
                              <span className="text-sm font-medium text-gray-900">{waiter.name}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{waiter.created_at ? new Date(waiter.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              {editingWaiterId === waiter.id ? (
                                <>
                                  <button onClick={handleSaveEdit} className="rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition" title="Save" disabled={loading}>✓</button>
                                  <button onClick={() => { setEditingWaiterId(null); setEditingWaiterName(''); }} className="rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition" title="Cancel" disabled={loading}>✕</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => handleEditWaiter(waiter)} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition" title="Edit" disabled={loading}>✎</button>
                                  <button onClick={() => handleDeleteWaiter(waiter.id)} className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition" title="Remove" disabled={loading}>✕</button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {currentPrintOrder && (
        <div style={{ position: 'fixed', left: 0, top: 0, width: '80mm', height: 'auto', visibility: 'hidden', overflow: 'visible', pointerEvents: 'none', zIndex: -1 }}>
          <OrderPrintContent ref={printRef} order={currentPrintOrder} onReady={() => setIsPrintReady(true)} />
        </div>
      )}
    </div>
  );
};

export default BranchManagerWaiterSection;
